/**
 * Pure helpers for ChangeMachineLocation.tsx, split out so they can be unit tested without
 * pulling in the RTK Query store setup that the modal component's other imports require.
 */

/**
 * Mutations reject with `FetchBaseQueryError | SerializedError`; this app's error payloads carry
 * `data.meta` (see RemoveMachine.tsx's identical extraction for the existing uninstall flow).
 */
export const errorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null) {
    const err = error as { data?: { meta?: string }; message?: string };
    return err.data?.meta || err.message || 'An unknown error occurred.';
  }
  return 'An unknown error occurred.';
};

/**
 * MachineDetail.location.id comes back as a string, and is "0" (a truthy string!) for an
 * uninstalled machine rather than absent -- see MACHINE_DETAILS' Location column in
 * constants/app/machine.tsx for the same check.
 */
export const parseCurrentLocationId = (locationId: string | undefined): number | undefined => {
  const parsed = Number.parseInt(locationId ?? '0', 10);
  return parsed === 0 ? undefined : parsed;
};

/**
 * Whether handleConfirmMove should uninstall the machine from its current location before
 * assigning it to the new one.
 *
 * False once `strandedAfterUninstall` is set: that flag only gets set after uninstall already
 * succeeded and the follow-up assign failed, so the machine has no active AtmLocation row left
 * to uninstall -- retrying the uninstall call would just 409 against atlas and the assign would
 * never run again. See DEV-2195 review: a retry from the stranded state must be assign-only.
 */
export const shouldUninstallBeforeAssign = (
  hasCurrentLocation: boolean,
  strandedAfterUninstall: boolean,
): boolean => hasCurrentLocation && !strandedAfterUninstall;

/**
 * Atlas deserializes `atm_ids` as `List<int>` always -- a bare number 422s ("could not be
 * converted to List<int>", INVALID_ATM_IDS). This must stay an array, even for a single atm,
 * matching InstallMachine.tsx's `atm_ids: selectedMachines.map(...)`.
 */
export const buildAssignPayload = (atmId: number, locationId: number) => ({
  location_id: locationId,
  atm_ids: [atmId],
  product_types: [] as string[],
});

/**
 * Default note text for the source location, pre-filling the note field so documenting "who
 * removed/relocated this machine and why" doesn't require a separate trip to Location single's
 * notes tab (see DEV-2195 follow-up: this was the whole point of the machine-first flow).
 * Editable by the operator before submitting.
 */
export const defaultRemovalNoteText = (atmId: number): string =>
  `Machine ${atmId} removed from this location.`;

export const defaultRelocationNoteText = (atmId: number, targetLocationName: string): string =>
  `Machine ${atmId} relocated to ${targetLocationName}.`;

/**
 * Address line for a row in the location picker.
 *
 * Deliberately does NOT read `full_address`: athena's LocationsListView (the DTO behind
 * POST /v2/locations, which the picker lists) has no such field -- it serializes the location's
 * full address as `address` instead (LocationsListView.cs: `Address = ...Location.FullAddress`).
 * The hand-authored `Location` type declares `full_address` as required, so reading it compiled
 * fine but rendered nothing, leaving the picker showing bare names. Composes the structured
 * fields first (same convention as the case/serviceTicket LocationSelect fields) and falls back
 * to `address`.
 */
export const formatLocationAddress = (location: {
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  address?: string | null;
}): string => {
  // State and zip read as one unit ("Portland, OR 97201"), not comma-separated.
  const stateZip = [location.state, location.zipcode].filter(Boolean).join(' ');
  const parts = [location.street_address, location.city, stateZip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : (location.address ?? '');
};
